import React, { useState } from 'react';
import { Check, Copy, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { copyText, formatUtc } from './adminApi';

/**
 * Shows an invite or password-reset link exactly once. The server keeps only a
 * hash of the token, so once this dialog is closed the link cannot be shown again
 * (issue a new one instead).
 */
export default function InviteLinkDialog({ invite, email, company, onClose }) {
  const [copied, setCopied] = useState(false);
  const isReset = invite?.purpose === 'reset';

  const copy = async () => {
    await copyText(invite.url, 'Link');
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <Dialog open={Boolean(invite)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="adm-dialog adm-dialog--wide" data-testid="invite-link-dialog">
        <DialogHeader>
          <DialogTitle>{isReset ? 'Password reset link' : `Invite link for ${company || 'the new organization'}`}</DialogTitle>
          <DialogDescription>
            {isReset
              ? `Send this link to ${email}. It lets them choose a new password.`
              : `Send this link to ${email}. They open it, choose a password and are signed in.`}
          </DialogDescription>
        </DialogHeader>
        {invite && (
          <div className="adm-form">
            <div>
              <label htmlFor="invite-url" className="adm-label">One-time link</label>
              <div className="adm-inline-form-row">
                <input id="invite-url" className="adm-input adm-mono" readOnly value={invite.url} onFocus={(e) => e.target.select()} data-testid="invite-url" />
                <Button type="button" onClick={copy} data-testid="invite-copy">
                  {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />} {copied ? 'Copied' : 'Copy link'}
                </Button>
              </div>
              <p className="adm-hint">Expires {formatUtc(invite.expires_at)} and works once.</p>
            </div>
            <p className="adm-note adm-note--warning" role="note" style={{ display: 'flex', gap: 8 }}>
              <ShieldAlert aria-hidden="true" style={{ width: 16, height: 16, flexShrink: 0, marginTop: 2 }} />
              <span>
                <strong>This is the only time the link is shown.</strong> Anyone who has it can set the password for {email}, so send it
                only to them. If it is lost or expires, issue a new one from the Tenants list.
              </span>
            </p>
          </div>
        )}
        <DialogFooter className="adm-dialog-footer">
          <Button type="button" onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
